<template>
  <transition name="modal">
    <div class="modal__backdrop" v-show="show" @click.prevent="conditionalClose">
      <div class="modal__wrapper">
        <section
          class="modal__container"
          role="dialog"
          :style="{ 'max-width': containerMaxWidth }"
          :aria-hidden="!show"
          @click.stop>
          <div role="document" ref="modalContent" style="outline:0">
            <slot></slot>
          </div>
        </section>
      </div>
    </div>
  </transition>
</template>

<script>
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks
} from 'body-scroll-lock'
import {
  debounce,
  getFocusableEls
} from './utils'

export default {
  name: 'Modal',
  
  data() {
    return {
      focusedElBeforeOpen: '',
      focusableEls: '',
      firstFocusableEl: '',
      lastFocusableEl: ''
    }
  },
  
  props: {
    show: {
      type: Boolean,
      default: false
    },
    restrictClose: {
      // to disable closing by backdrop click or hitting the escape key
      // (e.g. with the LEA terms of use modal)
      type: Boolean,
      default: false
    },
    containerMaxWidth: {
      type: String,
      default: '40em',
    },
  },
  
  methods: {
    close() {
      this.$emit('close')
    },
    conditionalClose() {
      if ( this.$props.restrictClose === false ) this.close()
    },

    freezeBody(e) {
      if ( !this.$el.contains(e.target) ) e.preventDefault()
    },
    
    trapTabs(e) {
      // one of the very few cases where this is a useful thing
      const handleBackwardTab = () => {
        if ( document.activeElement === this.firstFocusableEl ) {
          e.preventDefault()
          this.lastFocusableEl.focus()
        }
      }
      const handleForwardTab = () => {
        if ( document.activeElement === this.lastFocusableEl ) {
          e.preventDefault()
          this.firstFocusableEl.focus()
        }
      }
      if (e.keyCode === 9) {
        if ( this.focusableEls.length === 1 ) {
          e.preventDefault()
          return
        }
        e.shiftKey ? handleBackwardTab() : handleForwardTab()
      }
    },

    recalculateHeight() {
      let modalHeight = window.innerHeight * 0.9

      this.$nextTick(() => {
        this.$refs.modalContent.style.setProperty('max-height', `${modalHeight}px`)
      })
    }
  },
  
  watch: {
    show(isShowing) {
      if (isShowing) {
        disableBodyScroll(this.$el)
        this.focusedElBeforeOpen = document.activeElement

        this.$nextTick(() => {
          this.firstFocusableEl.focus()
          this.$el.addEventListener('keydown', this.trapTabs)
        })
      }
      else {
        this.close()
        enableBodyScroll(this.$el)

        if (this.focusedElBeforeOpen && this.focusedElBeforeOpen.focus) {
          this.focusedElBeforeOpen.focus()
        }

        this.$el.removeEventListener('keydown', this.trapTabs)
      }
    },
  },


  mounted() {
    this.$refs.modalContent.setAttribute('tabindex', '0')
    this.focusableEls = getFocusableEls(this.$el)
    this.firstFocusableEl = this.focusableEls[0]
    this.lastFocusableEl = this.focusableEls[ this.focusableEls.length - 1 ]
    
    if (this.show) {
      disableBodyScroll(this.$el)
      this.firstFocusableEl.focus()
      this.$el.addEventListener('keydown', this.trapTabs)
    }
    
    document.addEventListener('keydown', (e) => {
      if ( this.show && e.keyCode == 27 ) this.conditionalClose()
    })

    window.addEventListener('resize', debounce(this.recalculateHeight, 100))
  },

  destroyed() {
    clearAllBodyScrollLocks()
  },
  
}
</script>
